import { isDesktop, desktop } from '../desktop/bridge';

/**
 * Couche d'accès à l'API. Les composants passent par ici et nulle part
 * ailleurs : aucune règle métier ne vit côté navigateur.
 *
 * C'est aussi le seul endroit qui sache si l'on tourne dans le navigateur ou
 * dans l'application de bureau. Sur le web, un `fetch` vers `/api`. Sur le
 * bureau, le même chemin est confié au processus principal, qui répond depuis
 * la base locale ou relaie au serveur — les écrans, eux, ne voient aucune
 * différence et n'ont pas de branche « hors ligne ».
 */
const BASE = '/api';

/** Émis sur une réponse 401 en dehors de la connexion, pour vider la session. */
export const UNAUTHENTICATED_EVENT = 'diagflow:session-perdue';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ErrorBody = { erreur?: { code?: string; message?: string; details?: unknown } };

async function toApiError(response: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    // réponse non JSON : le statut reste la seule information disponible
    body = null;
  }

  const message =
    body?.erreur?.message ??
    `Le serveur a répondu ${response.status}. Réessayez ou signalez l'incident.`;
  return new ApiError(
    response.status,
    message,
    body?.erreur?.code ?? 'erreurInconnue',
    body?.erreur?.details,
  );
}

async function handle<T>(response: Response, path: string): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  const error = await toApiError(response);
  if (error.status === 401 && !path.startsWith('/auth/login')) {
    window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  }
  throw error;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Même traitement qu'une réponse HTTP, pour ce que rend le processus
 * principal : un statut et un corps, décodés par le même chemin. Une erreur
 * du bureau porte donc le même type et le même message qu'une erreur du
 * serveur, et les écrans n'ont rien à distinguer.
 */
async function sendThroughDesktop<T>(
  method: Method,
  path: string,
  body?: unknown,
): Promise<T> {
  const { status, body: payload } = await desktop().api(method, path, body);

  if (status >= 200 && status < 300) return payload as T;

  const erreur = (payload as ErrorBody | null)?.erreur;
  const error = new ApiError(
    status,
    erreur?.message ?? `Le serveur a répondu ${status}.`,
    erreur?.code ?? 'erreurInconnue',
    erreur?.details,
  );
  if (status === 401 && !path.startsWith('/auth/login')) {
    window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  }
  throw error;
}

async function send<T>(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // le pont ne connaît pas l'interruption : un appel local est immédiat, et
  // un appel relayé est borné par le délai du client du processus principal
  if (isDesktop()) return sendThroughDesktop<T>(method, path, body);

  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal !== undefined) init.signal = signal;

  const response = await fetch(`${BASE}${path}`, init);
  return handle<T>(response, path);
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return send<T>('GET', path, undefined, signal);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return send<T>('POST', path, body);
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return send<T>('PUT', path, body);
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return send<T>('PATCH', path, body);
}

export function apiDelete<T>(path: string): Promise<T> {
  return send<T>('DELETE', path);
}

/**
 * Téléversement d'un rapport. C'est la seule écriture qui porte un fichier, et
 * la seule que le mode hors ligne n'absorbe pas : l'empreinte du fichier doit
 * être confrontée à celle des imports déjà connus, et l'extraction a lieu au
 * serveur. Hors ligne, l'appel échoue en le disant plutôt que d'accepter un
 * import qui serait perdu.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  if (isDesktop()) {
    const file = form.get('file');
    if (!(file instanceof File)) {
      throw new ApiError(400, 'Aucun fichier à téléverser.', 'requeteInvalide');
    }
    const fields: Record<string, string> = {};
    for (const [key, value] of form.entries()) {
      if (typeof value === 'string') fields[key] = value;
    }
    const { status, body } = await desktop().api('UPLOAD', path, {
      fields,
      file: {
        name: file.name,
        type: file.type,
        bytes: new Uint8Array(await file.arrayBuffer()),
      },
    });
    if (status >= 200 && status < 300) return body as T;
    const erreur = (body as ErrorBody | null)?.erreur;
    throw new ApiError(
      status,
      erreur?.message ?? `Le serveur a répondu ${status}.`,
      erreur?.code ?? 'erreurInconnue',
      erreur?.details,
    );
  }

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  });
  return handle<T>(response, path);
}

/** Construit une chaîne de requête en ignorant les valeurs absentes. */
export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

/** Message affichable pour une erreur quelconque remontée d'un appel. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Erreur inattendue.';
}
